import { useState } from 'react';
import { Button } from '../../design-system/primitives/index.js';
import SwatchGrid from './SwatchGrid.jsx';
import { formatRecipeCount, packRecipeCount } from './packRecipeCount.js';
import './RecipePacks.css';

/**
 * PackCardCompact — unified compact pack card for two shelves:
 *   - Desktop AddViewMature "Grow your collection" 4-up grid (variant="desktop")
 *   - Mobile MobileAddMature horizontal shelf (variant="mobile", default)
 *
 * Structure (both variants): SwatchGrid · title · blurb · meta · button row.
 * variant controls swatch height, title typography, card shape, and the
 * fixed-width / flex-shrink needed for the mobile horizontal-scroll layout.
 *
 * Props:
 *   pack       — pack object ({ name, blurb, tag, swatches, ... })
 *   added      — boolean, replaces Add with a disabled ✓ Added button. Preview stays.
 *   onOpen     — opens PackDetailModal / PackDetailSheet
 *   onAdd      — fires the pack-add mutation directly (POST /curated-packs/{slug}/apply)
 *   variant    — 'mobile' (default) | 'desktop'
 */
export default function PackCardCompact({ pack, added, onOpen, onAdd, variant = 'mobile' }) {
  const [adding, setAdding] = useState(false);
  const isDesktop = variant === 'desktop';
  const recipeCount = packRecipeCount(pack);

  const handleAdd = async () => {
    if (adding || !onAdd) return;
    setAdding(true);
    try {
      await onAdd();
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className={isDesktop ? 'rp-card-mini' : 'rp-card-compact'}>
      <SwatchGrid swatches={pack.swatches} size={isDesktop ? 'xs' : 'md'} />
      <div>
        {isDesktop ? (
          <div className="rp-pack-card-title-compact">
            {pack.name}
          </div>
        ) : (
          <div className="rp-pack-card-title-display">
            {pack.name}
          </div>
        )}
        <div className="rp-card-compact-blurb">{pack.blurb}</div>
        <div className="rp-card-compact-meta">{formatRecipeCount(recipeCount)} · {pack.tag}</div>
      </div>
      <div className="rp-card-compact-actions">
        <Button
          type="button"
          variant="secondary"
          onClick={onOpen}
          className="rp-card-compact-preview-btn"
        >
          Preview
        </Button>
        {added ? (
          <Button
            type="button"
            variant="secondary"
            className="rp-card-compact-added-btn"
            disabled
          >
            ✓ Added
          </Button>
        ) : (
          <Button
            type="button"
            variant="primary"
            onClick={handleAdd}
            className="rp-card-compact-add-btn"
            disabled={adding}
            aria-busy={adding}
          >
            {adding ? 'Adding…' : `Add ${recipeCount} recipes`}
          </Button>
        )}
      </div>
    </div>
  );
}
